import React, { useMemo, useRef, type ReactNode } from 'react';

import type { PostOfficeUserInteractionHandlers } from '@atlassian/post-office-analytics/types';
import type { SerializableRecord } from '@atlassian/post-office-placement-data/types';

import type { PostOfficeMessageProps } from '../components/PostOfficeMessage';
import { PostOfficeMessage } from '../components/PostOfficeMessage';
import { transformPlacementMessageToMessageLifecycleProps } from '../util/transformPlacementMessageToMessageLifecycleProps';
import type {
	PlacementDataEndpointData,
	PlacementMessage,
	PlacementMessageDiscriminant,
	UsePlacementDataOptions,
} from './usePlacementData';
import { usePlacementData } from './usePlacementData';
import type { MatchBuilder, MatchBuilderChain, MatchBuilderInitialChain } from './utils';
import {
	PlacementLocationError,
	PlacementValidationError,
	createMatchBuilder,
	isMatchBuilder,
	isPlacementMessage,
	toHookError,
} from './utils';

/**
 * A `PostOfficeMessage` wrapper with `placementId` and `messageTemplateId` closed over.
 * `messageCategory` is closed over from the fetched message.
 * Supply `messageInstanceId` and `children` per-instance when iterating.
 *
 * @example
 * ```tsx
 * const { messageInstanceIds, PostOfficeMessageInstance } = useMessageInstances({ messageTemplateId });
 * return messageInstanceIds.map((messageInstanceId) => (
 *   <PostOfficeMessageInstance key={messageInstanceId} messageInstanceId={messageInstanceId}>
 *     {(message) => <MyUI content={message.context} />}
 *   </PostOfficeMessageInstance>
 * ));
 * ```
 *
 * @see https://developer.atlassian.com/platform/post-office/analytics-and-reporting/instrumenting-user-interactions/
 */
export type PostOfficeMessageInstance<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = (
	props: Omit<
		PostOfficeMessageProps,
		'placementId' | 'messageTemplateId' | 'messageCategory' | 'children'
	> & {
		/**
		 * Receives this message instance, plus the Post Office user-interaction handlers bound to
		 * it — so a headless consumer records what the user decided without wiring up analytics:
		 *
		 * ```tsx
		 * {(message, interactions) => {
		 *   const { onCtaActioned } = interactions!;
		 *   return <MyUI onTry={() => onCtaActioned({ interactionIdentifier: 'tryIt' })} />;
		 * }}
		 * ```
		 *
		 * `interactions` is always supplied at runtime. The `?` is temporary and will be removed
		 * once existing call sites are migrated — assert once when destructuring rather than
		 * optional-chaining every call.
		 */
		children: (
			message: PlacementMessage<TMessage>,
			interactions?: PostOfficeUserInteractionHandlers,
		) => ReactNode;
	},
) => JSX.Element | null;

export type UseMessageInstancesResult<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = {
	/**
	 * All non-dismissed message instances for the specified template.
	 * Filtered reactively when `dismissMessage` is called.
	 *
	 * Only present when `dataOutsideWrapper` is explicitly `true`. When `false` (the default),
	 * {@link UseMessageInstancesResultWithoutData} is returned.
	 */
	messages: Array<PlacementMessage<TMessage>>;
	/**
	 * The `messageInstanceId` for all non-dismissed message instances for the specified template.
	 * Filtered reactively when `dismissMessage` is called.
	 */
	messageInstanceIds: Array<string>;
	/**
	 * A `PostOfficeMessage` wrapper with `placementId` and `messageTemplateId` closed over.
	 * `messageCategory` is closed over from the fetched message.
	 * Pass `messageInstanceId` and `children` for each message when iterating.
	 */
	PostOfficeMessageInstance: PostOfficeMessageInstance<TMessage>;
	/**
	 * The set of dismissed messageInstanceIds across the whole placement.
	 */
	dismissedIds: ReadonlySet<string>;
	/** Whether the initial Placement API fetch is in progress. */
	isLoading: boolean;
	/** Any error returned by the Placement API fetch or validation. */
	error: Error | undefined;
	/**
	 * Dismisses a message by its instance ID.
	 * Updates `messages` and `dismissedIds` immediately via pub/sub.
	 */
	dismissMessage: (messageInstanceId: string) => void;
};

/**
 * Return type of `useMessageInstances` when `dataOutsideWrapper` is `false`.
 * Identical to {@link UseMessageInstancesResult} but without `messages`. Message data can
 * be accessed inside the `PostOfficeMessageInstance` render prop.
 */
export type UseMessageInstancesResultWithoutData<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = Omit<UseMessageInstancesResult<TMessage>, 'messages'>;

export type UseMessageInstancesMatchHelpers<
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
> = {
	data: PlacementDataEndpointData<TMessage, TExtension>;
	/** Fluent builder for composing message filters. */
	matchBuilder: MatchBuilderInitialChain<TMessage, TExtension>;
};

export type UseMessageInstancesOptions<
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
> = {
	/**
	 * For context there are 3 migration states:
	 * - have-i-seen-it only: The message is exclusively relying on have-i-seen-it.
	 * - have-i-seen-it + post-office: The message is relying on have-i-seen-it + post-office.
	 * - post-office only: The message is exclusively relying on post-office.
	 *
	 * Important note: Post Office is called in every migration state.
	 * The only thing that changes is when the service responses are considered / relied upon.
	 * This consideration is managed by the message maker via 2 feature gates.
	 *
	 * Because of this setup, there's the risk that when a message maker implements the migration set-up in the front-end for the first time,
	 * they may make too many calls to post-office. At which point, the only mitigation is to revert the code.
	 *
	 * This is not a significant risk, the additional traffic is expected to be +2%.
	 *
	 * In any case, this governance gives us some form of a safety net.
	 *
	 * As this risk is only realized when the message maker implements the migration set-up in the front-end for the first time,
	 * they are only subject to governance if the two feature gates are off.
	 *
	 * When provided to `useMessageData`, the selected `messageTemplateId` is also sent to the
	 * Placement API as `hisiMigrationTemplateId` when the HISI migration query-param gate and
	 * headless query-string support are enabled.
	 *
	 */
	hisiMigrationGovernance?: {
		bothModeFgEnabled: boolean;
		poOnlyFgEnabled: boolean;
	};
	/**
	 * Custom function to extract the candidate message list before `messageTemplateId` filtering.
	 *
	 * Internally read via a ref, so **inline arrow functions are safe** — the latest value is always
	 * used without causing the internal `useMemo` to re-run on every render.
	 */
	match?: (
		args: UseMessageInstancesMatchHelpers<TMessage, TExtension>,
	) =>
		| Array<PlacementMessage<TMessage>>
		| MatchBuilderChain<TMessage, TExtension>
		| MatchBuilderInitialChain<TMessage, TExtension>;
	/**
	 * Optional custom validator. Defaults to structural `isPlacementMessage` check when omitted.
	 *
	 * Internally read via a ref, so **inline arrow functions are safe** — the latest value is always
	 * used without causing the internal `useMemo` to re-run on every render.
	 */
	validate?: (message: unknown) => message is PlacementMessage<TMessage>;
} & UsePlacementDataOptions;

/**
 * Returns all non-dismissed instances for a specific message template.
 *
 * Use this hook when your message template is backed by an **implicit message creator** that can
 * produce more than one instance at a time (e.g. a triggered message that fires once per Jira issue).
 * In that case, the Placement API may return multiple instances with the same `messageTemplateId`,
 * and this hook surfaces all of them.
 *
 * If your implicit message creator can only ever produce a single instance, prefer
 * {@link useMessageData} which returns `message` (singular) and handles the single-instance case
 * without needing to iterate over an array.
 *
 * By default, iterate the returned `messageInstanceIds` and render each instance through the
 * `PostOfficeMessageInstance` wrapper, reading the message data from its render prop. This is the
 * recommended usage — you get each message's data without opting into `dataOutsideWrapper`:
 *
 * ```tsx
 * const { messageInstanceIds, PostOfficeMessageInstance } = useMessageInstances({
 *   messageTemplateId: 'some-template',
 * });
 * return messageInstanceIds.map((messageInstanceId) => (
 *   <PostOfficeMessageInstance key={messageInstanceId} messageInstanceId={messageInstanceId}>
 *     {(message) => <MyUI content={message.context} />}
 *   </PostOfficeMessageInstance>
 * ));
 * ```
 *
 * Provide `options.match` when messages live under a non-standard key or you need to filter the
 * candidate messages before the `messageTemplateId` match is applied:
 *
 * ```tsx
 * const { messageInstanceIds } = useMessageInstances(
 *   { messageTemplateId: 'some-template' },
 *   { match: ({ matchBuilder }) => matchBuilder.messageLocation('triggeredMessages') },
 * );
 * ```
 */
export function useMessageInstances<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(
	selector: { placementId?: string; messageTemplateId: TMessage['messageTemplateId'] },
	options?: UseMessageInstancesOptions<TMessage, TExtension> & {
		dataOutsideWrapper?: false | undefined;
	},
): UseMessageInstancesResultWithoutData<TMessage>;
export function useMessageInstances<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(
	selector: { placementId?: string; messageTemplateId: TMessage['messageTemplateId'] },
	options: UseMessageInstancesOptions<TMessage, TExtension> & {
		dataOutsideWrapper: true;
	},
): UseMessageInstancesResult<TMessage>;
export function useMessageInstances<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(
	selector: { placementId?: string; messageTemplateId: TMessage['messageTemplateId'] },
	options?: UseMessageInstancesOptions<TMessage, TExtension> & { dataOutsideWrapper?: boolean },
): UseMessageInstancesResult<TMessage> | UseMessageInstancesResultWithoutData<TMessage> {
	const { placementId = 'headless', messageTemplateId } = selector;
	const { match, validate, dataSource, queryString } = options ?? {};

	const dataOutsideWrapper = options?.dataOutsideWrapper ?? false;

	// Store match/validate in refs so inline arrow functions passed by callers don't cause
	// the useMemo to re-run on every render. The latest value is always read inside the memo.
	const matchRef = useRef(match);
	matchRef.current = match;
	const validateRef = useRef(validate);
	validateRef.current = validate;

	const {
		data,
		dismissedIds,
		isLoading,
		error: fetchError,
		dismissMessage,
	} = usePlacementData<TMessage, TExtension>(placementId, { dataSource, queryString });

	const { templateMessages, validationError } = useMemo<{
		templateMessages: Array<PlacementMessage<TMessage>>;
		validationError: Error | undefined;
	}>(() => {
		if (!data) {
			return { templateMessages: [], validationError: undefined };
		}

		let candidates: Array<PlacementMessage<TMessage>>;
		try {
			const result = matchRef.current
				? matchRef.current({ data, matchBuilder: createMatchBuilder<TMessage, TExtension>() })
				: (data.messages ?? []);
			const resolved = isMatchBuilder(result)
				? (result as MatchBuilder<TMessage, TExtension>).match(data)
				: (result as Array<PlacementMessage<TMessage>>);
			candidates = resolved;
		} catch (err) {
			if (err instanceof PlacementLocationError) {
				return { templateMessages: [], validationError: err };
			}
			return { templateMessages: [], validationError: toHookError(err) };
		}

		const templateMatches = candidates.filter((m) => m.messageTemplateId === messageTemplateId);

		const guard = validateRef.current ?? isPlacementMessage;
		let failedCount: number;
		try {
			failedCount = templateMatches.filter((m) => !guard(m)).length;
		} catch (err) {
			return { templateMessages: [], validationError: toHookError(err) };
		}
		if (failedCount > 0) {
			return {
				templateMessages: [],
				validationError: new PlacementValidationError({ placementId, failedCount }),
			};
		}

		return { templateMessages: templateMatches, validationError: undefined };
	}, [data, messageTemplateId, placementId]);

	const messages = useMemo(() => {
		return templateMessages.filter((m) => !dismissedIds.has(m.messageInstanceId));
	}, [dismissedIds, templateMessages]);

	const messageInstanceIds = useMemo(() => messages.map((m) => m.messageInstanceId), [messages]);

	// Build lookup from validated template data (not dismissed-filtered messages)
	// so dismissals and data refreshes don't change PostOfficeMessageInstance identity and remount callers.
	const messageByMessageInstanceId = useMemo<Record<string, PlacementMessage<TMessage>>>(() => {
		const lookup: Record<string, PlacementMessage<TMessage>> = {};

		for (const message of templateMessages) {
			lookup[message.messageInstanceId] = message;
		}

		return lookup;
	}, [templateMessages]);
	const messageByMessageInstanceIdRef = useRef<Record<string, PlacementMessage<TMessage>>>({});
	messageByMessageInstanceIdRef.current = messageByMessageInstanceId;

	// Single wrapper closed over placementId + messageTemplateId.
	// messageInstanceId is passed per-render by the caller.
	const PostOfficeMessageInstance = useMemo<PostOfficeMessageInstance<TMessage>>(() => {
		return ({ children, ...props }) => {
			const message = messageByMessageInstanceIdRef.current[props.messageInstanceId];
			const messageCategory = message?.messageCategory;

			return (
				<PostOfficeMessage
					placementId={placementId}
					messageTemplateId={messageTemplateId}
					messageCategory={messageCategory}
					{...transformPlacementMessageToMessageLifecycleProps({ message })}
					{...props}
				>
					{(interactions) => children(message, interactions)}
				</PostOfficeMessage>
			);
		};
	}, [messageTemplateId, placementId]);

	return dataOutsideWrapper
		? {
				messages,
				messageInstanceIds,
				PostOfficeMessageInstance,
				dismissedIds,
				isLoading,
				error: fetchError ?? validationError,
				dismissMessage,
			}
		: {
				messageInstanceIds,
				PostOfficeMessageInstance,
				dismissedIds,
				isLoading,
				error: fetchError ?? validationError,
				dismissMessage,
			};
}
